// Laporan Pengesahan (Prompt F) — jejak audit kelulusan/penolakan bukti bayaran.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Printer, Loader2, FileCheck2 } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { formatRM } from '../../../lib/format';
import { toCsv, downloadCsv, printLaporan, todayStr, monthStartStr } from '../../../lib/laporan';
import PageHeader from '../../../components/PageHeader';
import { CardSkeleton } from '../../../components/Skeletons';
import DataTable from '../../../components/DataTable';
import StatusPill from '../../../components/StatusPill';
import { Button } from '../../../components/ui/button';

export default function LaporanPengesahan() {
  const { t } = useTranslation();
  const [dari, setDari] = useState(monthStartStr());
  const [hingga, setHingga] = useState(todayStr());
  const [status, setStatus] = useState('semua');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('payment_proofs')
      .select('id, nama_file, status, alasan_tolak, created_at, updated_at, diverifikasi_oleh, invoice_id, student_id')
      .in('status', ['lulus', 'tolak'])
      .gte('updated_at', dari).lte('updated_at', hingga + 'T23:59:59')
      .order('updated_at', { ascending: false });
    if (status !== 'semua') q = q.eq('status', status);
    const { data: proofs } = await q;
    const sIds = [...new Set((proofs || []).map((p) => p.student_id).filter(Boolean))];
    const iIds = [...new Set((proofs || []).map((p) => p.invoice_id).filter(Boolean))];
    const uIds = [...new Set((proofs || []).map((p) => p.diverifikasi_oleh).filter(Boolean))];
    const [{ data: students }, { data: invoices }, { data: users }] = await Promise.all([
      sIds.length ? supabase.from('students').select('id, nama, class_id').in('id', sIds) : { data: [] },
      iIds.length ? supabase.from('invoices').select('id, jumlah_bayar_rm').in('id', iIds) : { data: [] },
      uIds.length ? supabase.from('profiles').select('id, nama').in('id', uIds) : { data: [] },
    ]);
    const sMap = Object.fromEntries((students || []).map((s) => [s.id, s]));
    const iMap = Object.fromEntries((invoices || []).map((i) => [i.id, i]));
    const uMap = Object.fromEntries((users || []).map((u) => [u.id, u.nama]));
    const cIds = [...new Set((students || []).map((s) => s.class_id).filter(Boolean))];
    const { data: classes } = cIds.length ? await supabase.from('classes').select('id, nama').in('id', cIds) : { data: [] };
    const cMap = Object.fromEntries((classes || []).map((c) => [c.id, c.nama]));
    setRows((proofs || []).map((p) => ({
      ...p,
      pelajar: sMap[p.student_id]?.nama || '-',
      kelas: cMap[sMap[p.student_id]?.class_id] || '-',
      jumlah: Number(iMap[p.invoice_id]?.jumlah_bayar_rm || 0),
      penyemak: uMap[p.diverifikasi_oleh] || '-',
    })));
    setLoading(false);
  }, [dari, hingga, status]);

  useEffect(() => { load(); }, [load]);
  const counts = useMemo(() => ({
    lulus: rows.filter((r) => r.status === 'lulus').length,
    tolak: rows.filter((r) => r.status === 'tolak').length,
    jumlah: rows.reduce((a, r) => a + r.jumlah, 0),
  }), [rows]);

  const doCsv = () => downloadCsv(`laporan-pengesahan-${dari}_${hingga}.csv`, toCsv(rows, [
    { label: 'Tarikh semakan', get: (r) => new Date(r.updated_at).toLocaleString('ms-MY') },
    { label: 'Pelajar', get: (r) => r.pelajar },
    { label: 'Kelas', get: (r) => r.kelas },
    { label: 'Jumlah bil (RM)', get: (r) => r.jumlah.toFixed(2) },
    { label: 'Status', get: (r) => r.status },
    { label: 'Disemak oleh', get: (r) => r.penyemak },
    { label: 'Alasan tolak', get: (r) => r.alasan_tolak || '' },
  ]));

  const inputCls = 'h-10 px-3 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand';

  return (
    <div>
      <div className="print:hidden">
        <PageHeader title={t('lapPengesahan')} subtitle={t('lapPengesahanSub')} breadcrumbs={[{ label: t('laporan') }, { label: t('lapPengesahan') }]} />
      </div>
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-black">{t('lapPengesahan')}</h1>
        <p className="text-sm text-muted-foreground">{dari} → {hingga}</p>
      </div>

      <div className="print:hidden mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-muted-foreground">{t('dari')}<input type="date" value={dari} onChange={(e) => setDari(e.target.value)} className={`${inputCls} ml-2`} /></label>
        <label className="text-xs font-semibold text-muted-foreground">{t('hingga')}<input type="date" value={hingga} onChange={(e) => setHingga(e.target.value)} className={`${inputCls} ml-2`} /></label>
        <label className="text-xs font-semibold text-muted-foreground">{t('status')}
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} ml-2`}>
            <option value="semua">{t('semua')}</option>
            <option value="lulus">{t('luluskan')}</option>
            <option value="tolak">{t('tolak')}</option>
          </select>
        </label>
        <Button onClick={load} className="h-10 bg-brand text-white hover:bg-brand-royal">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('tapis')}</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={doCsv} className="h-10"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button variant="outline" onClick={() => printLaporan(t('lapPengesahan'))} className="h-10"><Printer className="h-4 w-4 mr-1.5" />{t('cetak')}</Button>
        </div>
      </div>

      {loading ? <CardSkeleton count={3} /> : (
        <DataTable
          testid="lap-pengesahan"
          rows={rows}
          tabs={[
            { key: 'semua', label: t('semua') },
            { key: 'lulus', label: `${t('stLulus')} · ${counts.lulus}`, filter: (r) => r.status === 'lulus' },
            { key: 'tolak', label: `${t('stTolak')} · ${counts.tolak}`, filter: (r) => r.status === 'tolak' },
          ]}
          searchKeys={[(r) => r.pelajar, (r) => r.kelas, (r) => r.penyemak]}
          columns={[
            { key: 'tarikh', header: t('tarikh'), sortable: true, sortValue: (r) => r.updated_at, className: 'whitespace-nowrap',
              render: (r) => new Date(r.updated_at).toLocaleDateString('ms-MY', { dateStyle: 'medium' }) },
            { key: 'pelajar', header: t('pelajar'), sortable: true, sortValue: (r) => r.pelajar, render: (r) => <span className="block truncate font-medium text-foreground">{r.pelajar}</span> },
            { key: 'kelas', header: t('kelas'), sortable: true, sortValue: (r) => r.kelas, render: (r) => r.kelas },
            { key: 'jumlah', header: t('jumlah'), align: 'right', sortable: true, sortValue: (r) => r.jumlah,
              render: (r) => <span className="font-mono tabular">{formatRM(r.jumlah)}</span> },
            { key: 'status', header: t('status'), render: (r) => <StatusPill status={r.status} /> },
            { key: 'penyemak', header: t('disemakOleh'), sortable: true, sortValue: (r) => r.penyemak, render: (r) => r.penyemak },
          ]}
          empty={{ icon: FileCheck2, title: t('tiadaData'), description: t('tiadaDataDesc') }}
        />
      )}
    </div>
  );
}
